import React, { useState, useEffect } from 'react';
import { Settings2, Plus, Trash2, Save } from 'lucide-react';
import { Button } from './ui/button';
import { sitesAPI, formatApiError } from '../utils/api';
import { toast } from 'sonner';

const INDIAN_STATES = [
  'Karnataka', 'Madhya Pradesh', 'Maharashtra', 'Tamil Nadu', 'Gujarat', 'Rajasthan',
  'Uttar Pradesh', 'Haryana', 'Punjab', 'Telangana', 'Andhra Pradesh', 'Kerala',
  'West Bengal', 'Delhi', 'Odisha', 'Bihar',
];

export default function SiteReportingConfigCard({ siteId, onSaved }) {
  const [site, setSite] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await sitesAPI.getById(siteId);
        if (cancelled) return;
        const s = res.data;
        setSite(s);
        setForm({
          commissioned_date: (s.commissioned_date || s.commissioned_at || '').slice(0, 10),
          state: s.state || '',
          discom_name: s.discom_name || '',
          internal_asset_ref: s.internal_asset_ref || '',
          dg_kwh_per_litre: s.dg_kwh_per_litre ?? 3.5,
          soc_comfort_floor_pct: s.soc_comfort_floor_pct ?? 40,
          tod_peak_rate_inr_kwh: s.tod_peak_rate_inr_kwh ?? '',
          tod_offpeak_rate_inr_kwh: s.tod_offpeak_rate_inr_kwh ?? '',
          tod_peak_windows: s.tod_peak_windows?.length ? s.tod_peak_windows : [],
        });
      } catch (err) {
        toast.error(formatApiError(err, 'Failed to load site reporting config'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [siteId]);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const daysInService = (() => {
    if (!form?.commissioned_date) return null;
    const d = new Date(`${form.commissioned_date}T00:00:00Z`);
    if (Number.isNaN(d.getTime())) return null;
    return Math.floor((Date.now() - d.getTime()) / 86400000);
  })();

  const addWindow = () => setForm((f) => ({ ...f, tod_peak_windows: [...f.tod_peak_windows, { start: '18:00', end: '22:00' }] }));
  const removeWindow = (idx) => setForm((f) => ({ ...f, tod_peak_windows: f.tod_peak_windows.filter((_, i) => i !== idx) }));
  const updateWindow = (idx, key, value) => setForm((f) => ({
    ...f,
    tod_peak_windows: f.tod_peak_windows.map((w, i) => (i === idx ? { ...w, [key]: value } : w)),
  }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...site,
        commissioned_date: form.commissioned_date || null,
        state: form.state || null,
        discom_name: form.discom_name || null,
        internal_asset_ref: form.internal_asset_ref || null,
        dg_kwh_per_litre: parseFloat(form.dg_kwh_per_litre) || 3.5,
        soc_comfort_floor_pct: parseFloat(form.soc_comfort_floor_pct) || 40,
        tod_peak_rate_inr_kwh: form.tod_peak_rate_inr_kwh === '' ? null : parseFloat(form.tod_peak_rate_inr_kwh),
        tod_offpeak_rate_inr_kwh: form.tod_offpeak_rate_inr_kwh === '' ? null : parseFloat(form.tod_offpeak_rate_inr_kwh),
        tod_peak_windows: form.tod_peak_windows,
      };
      const res = await sitesAPI.update(siteId, payload);
      setSite(res.data);
      toast.success('Reporting configuration saved');
      onSaved?.(res.data);
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to save reporting configuration'));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !form) {
    return (
      <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="site-reporting-config-card">
        <div className="text-sm text-slate-400">Loading reporting configuration…</div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="site-reporting-config-card">
      <div className="flex items-center space-x-2 mb-4">
        <Settings2 className="w-5 h-5 text-blue-600" />
        <h3 className="font-semibold text-slate-900">Reporting Configuration</h3>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        Site onboarding inputs that drive days in service, diesel-avoided and tariff-arbitrage
        figures in the daily and monthly performance reports.
      </p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <div>
          <label className="text-[10px] text-slate-500 uppercase block mb-1">Commissioning date</label>
          <input type="date" value={form.commissioned_date} max={new Date().toISOString().slice(0, 10)}
            onChange={(e) => setField('commissioned_date', e.target.value)}
            className="w-full text-sm border border-slate-200 rounded px-2 py-1.5"
            data-testid="config-commissioned-date-input" />
          <div className="text-[10px] text-slate-400 mt-1" data-testid="config-days-in-service">
            {daysInService === null
              ? 'Not set — days in service and lifetime figures show a dash'
              : `${daysInService.toLocaleString()} days in service`}
          </div>
        </div>
        <div>
          <label className="text-[10px] text-slate-500 uppercase block mb-1">State (diesel pricing)</label>
          <select value={form.state} onChange={(e) => setField('state', e.target.value)}
            className="w-full text-sm border border-slate-200 rounded px-2 py-1.5" data-testid="config-state-select">
            <option value="">Not set</option>
            {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] text-slate-500 uppercase block mb-1">Distribution utility</label>
          <input type="text" value={form.discom_name} onChange={(e) => setField('discom_name', e.target.value)}
            placeholder="BESCOM" className="w-full text-sm border border-slate-200 rounded px-2 py-1.5" data-testid="config-discom-input" />
        </div>
        <div>
          <label className="text-[10px] text-slate-500 uppercase block mb-1">Internal asset ref</label>
          <input type="text" value={form.internal_asset_ref} onChange={(e) => setField('internal_asset_ref', e.target.value)}
            placeholder="BESS-VT-0417" className="w-full text-sm border border-slate-200 rounded px-2 py-1.5" data-testid="config-asset-ref-input" />
        </div>
        <div>
          <label className="text-[10px] text-slate-500 uppercase block mb-1">DG efficiency (kWh/L)</label>
          <input type="number" step="0.1" value={form.dg_kwh_per_litre} onChange={(e) => setField('dg_kwh_per_litre', e.target.value)}
            className="w-full text-sm border border-slate-200 rounded px-2 py-1.5" data-testid="config-dg-efficiency-input" />
        </div>
        <div>
          <label className="text-[10px] text-slate-500 uppercase block mb-1">SoC comfort floor (%)</label>
          <input type="number" step="1" value={form.soc_comfort_floor_pct} onChange={(e) => setField('soc_comfort_floor_pct', e.target.value)}
            className="w-full text-sm border border-slate-200 rounded px-2 py-1.5" data-testid="config-soc-floor-input" />
        </div>
        <div>
          <label className="text-[10px] text-slate-500 uppercase block mb-1">TOD peak rate (₹/kWh)</label>
          <input type="number" step="0.01" value={form.tod_peak_rate_inr_kwh} onChange={(e) => setField('tod_peak_rate_inr_kwh', e.target.value)}
            placeholder="Not configured" className="w-full text-sm border border-slate-200 rounded px-2 py-1.5" data-testid="config-tod-peak-rate-input" />
        </div>
        <div>
          <label className="text-[10px] text-slate-500 uppercase block mb-1">TOD off-peak rate (₹/kWh)</label>
          <input type="number" step="0.01" value={form.tod_offpeak_rate_inr_kwh} onChange={(e) => setField('tod_offpeak_rate_inr_kwh', e.target.value)}
            placeholder="Not configured" className="w-full text-sm border border-slate-200 rounded px-2 py-1.5" data-testid="config-tod-offpeak-rate-input" />
        </div>
      </div>

      <div className="mb-4">
        <label className="text-[10px] text-slate-500 uppercase block mb-1.5">TOD peak windows (local time)</label>
        <div className="space-y-2">
          {form.tod_peak_windows.map((w, idx) => (
            <div key={idx} className="flex items-center gap-2" data-testid={`config-tod-window-row-${idx}`}>
              <input type="time" value={w.start} onChange={(e) => updateWindow(idx, 'start', e.target.value)}
                className="text-sm border border-slate-200 rounded px-2 py-1" data-testid={`config-tod-window-start-${idx}`} />
              <span className="text-xs text-slate-400">to</span>
              <input type="time" value={w.end} onChange={(e) => updateWindow(idx, 'end', e.target.value)}
                className="text-sm border border-slate-200 rounded px-2 py-1" data-testid={`config-tod-window-end-${idx}`} />
              <button onClick={() => removeWindow(idx)} className="text-slate-400 hover:text-red-500" data-testid={`config-tod-window-remove-${idx}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          {form.tod_peak_windows.length === 0 && (
            <div className="text-xs text-slate-400">No peak windows configured — tariff arbitrage disabled for this site.</div>
          )}
          <Button size="sm" variant="outline" onClick={addWindow} data-testid="config-add-tod-window-btn">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Add window
          </Button>
        </div>
      </div>

      <Button onClick={handleSave} disabled={saving} data-testid="save-reporting-config-btn">
        <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving…' : 'Save configuration'}
      </Button>
    </div>
  );
}
